import { useState, useEffect } from 'react';
import { useToast } from '../components/Toast';
import {
  getEvents,
  toggleAttendance,
  addEvent,
  searchEvents,
  getMembers,
  getMyMember,
} from '../lib/data';
import EventCard from '../components/EventCard';
import { getSession } from '../lib/auth';

const USE_MOCK_DATA = String(import.meta?.env?.VITE_ENABLE_MOCK_DATA ?? 'false').toLowerCase() === 'true';

export default function Events() {
  const { showToast } = useToast();
  const [events, setEvents] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [timeframe, setTimeframe] = useState('6weeks');

  const [hoverEvent, setHoverEvent] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [attendanceKey, setAttendanceKey] = useState(USE_MOCK_DATA ? 'user1' : null);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [mobilePanel, setMobilePanel] = useState('timeline'); // 'timeline' | 'search'
  const [selectedSearchEvent, setSelectedSearchEvent] = useState(null);
  const [isAddingSearchEvent, setIsAddingSearchEvent] = useState(false);
  const [selectedTimelineEvent, setSelectedTimelineEvent] = useState(null);

  const [membersById, setMembersById] = useState({});

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const initializeUser = async () => {
      const session = await getSession();
      const role = session?.user?.role ?? 'prospect';

      // NOTE: event attendance must be keyed by *member id*, not auth user id,
      // because the API uses members as attendees.
      let memberId = session?.user?.id ? String(session.user.id) : null;
      try {
        const me = await getMyMember();
        if (me?.id) memberId = String(me.id);
      } catch {
        // fallback to session user id
      }

      setUserRole(role);
      setCurrentUserId(memberId);
      setAttendanceKey(USE_MOCK_DATA ? 'user1' : memberId);
    };
    initializeUser();
  }, []);

  useEffect(() => {
    if (userRole) {
      loadEvents();
      loadMembers();
    }
  }, [userRole]);

  const filterEventsByRole = (events) => {
    if (!events) return [];
    if (!userRole) return events;

    const role = String(userRole).toLowerCase();

    return events.filter(event => {
      const typeLower = String(event.type ?? '').toLowerCase();

      if (role === 'prospect') {
        return event.type === 'Public' || event.type === 'External';
      }
      // Regular members should not see executive-only events.
      if (role === 'member') {
        return !typeLower.includes('exec');
      }
      // exec/admin: can see everything.
      return true;
    });
  };

  const loadEvents = async () => {
    console.log('VITE_ENABLE_MOCK_DATA:', import.meta.env.VITE_ENABLE_MOCK_DATA);
    try {
      console.log('Calling getEvents...');
      const { events: newEvents } = await getEvents(0, 20);
      console.log('getEvents returned:', newEvents);
      setEvents(filterEventsByRole(newEvents));
      console.log('Filtered events:', filterEventsByRole(newEvents));
    } catch (err) {
      const msg = 'Failed to load events';
      showToast(msg, { kind: 'error' });
      console.error('Error loading events:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMembers = async () => {
    try {
      const { members } = await getMembers({ offset: 0, limit: 250 });
      const map = {};
      (members || []).forEach((m) => {
        if (!m?.id) return;
        map[String(m.id)] = m;
      });
      setMembersById(map);
    } catch {
      setMembersById({});
    }
  };
  const handleSearch = async (e) => {
    e.preventDefault();

    const criterion = String(searchQuery ?? '').trim();

    // Default search framework (keeps UX working even when the user just hits Search).
    // NOTE: This is intentionally a simple v1 prompt; the backend search endpoint is
    // a seam and may later swap to a real search provider.
    const q = criterion
      ? `business event in nanaimo BC for now to <search framework> about or related to ${criterion}`
      : 'business event in nanaimo BC for now to <search framework>';

    setIsSearchActive(true);
    setIsSearching(true);
    try {
      const results = await searchEvents(q, timeframe);
      const filtered = filterEventsByRole(results);

      // Don’t show candidates that are already in our main list.
      // Primary key: id (preferred).
      // Fallback: same title + same date-day (ISO) for cases where ids diverge.
      const existingIds = new Set((events || []).map((e) => String(e?.id ?? '')));
      const existingByTitleDay = new Set(
        (events || []).map((e) => {
          const title = String(e?.title ?? '').trim().toLowerCase();
          const day = e?.date ? String(e.date).slice(0, 10) : '';
          return `${title}::${day}`;
        })
      );

      const deduped = (filtered || []).filter((e) => {
        const id = String(e?.id ?? '');
        if (id && existingIds.has(id)) return false;

        const title = String(e?.title ?? '').trim().toLowerCase();
        const day = e?.date ? String(e.date).slice(0, 10) : '';
        if (title && day && existingByTitleDay.has(`${title}::${day}`)) return false;

        return true;
      });

      setSearchResults(deduped);
      setSelectedSearchEvent(null);

      if (deduped) {
        setMobilePanel('search');
      }
      if (!deduped?.length) {
        showToast('No new events found for that search.', { kind: 'info' });
      }
    } catch (err) {
      const msg = err?.message || 'Failed to search events';
      showToast(msg, { kind: 'error' });
      console.error('Search error:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchResults(null);
    setSearchQuery('');
    setIsSearchActive(false);
    setSelectedSearchEvent(null);
    setMobilePanel('timeline');
  };

  // Handle clicking outside search area
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchSection = document.querySelector('.search-section');
      if (searchSection && !searchSection.contains(event.target) && !event.target.closest('.input-field')) {
        setIsSearchActive(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddEvent = async (eventId) => {
    try {
      const result = await addEvent(eventId);
      if (!result.success) {
        const msg = result.message;
        showToast(msg, { kind: 'error' });
        return;
      }
      showToast('Event added.', { kind: 'success' });

      // Hide the added candidate from current search results (no need to re-search).
      setSearchResults((prev) => (Array.isArray(prev)
        ? prev.filter((item) => String(item?.id ?? '') !== String(eventId))
        : prev));
      setSelectedSearchEvent((prev) => (
        prev && String(prev?.id ?? '') === String(eventId) ? null : prev
      ));

      // Refresh timeline.
      loadEvents();
    } catch (err) {
      const msg = err?.message || 'Failed to add event';
      showToast(msg, { kind: 'error' });
    }
  };

  const handleAddSelectedSearchEvent = async () => {
    const eventId = selectedSearchEvent?.id;
    if (!eventId) return;

    try {
      setIsAddingSearchEvent(true);
      await handleAddEvent(eventId);
      setSelectedSearchEvent(null);
    } finally {
      setIsAddingSearchEvent(false);
    }
  };

  const handleAttendance = async (eventId) => {
    try {
      const event = events.find(e => e.id === eventId);
      if (!event) return;

      const typeLower = String(event.type ?? '').toLowerCase();
      const isExternal = typeLower === 'external' || typeLower === 'public';
      if (!isExternal) {
        showToast('Attendance for internal events is recorded via meeting QR sign-in.', { kind: 'info' });
        return;
      }

      if (!attendanceKey && !currentUserId) {
        const msg = 'Please sign in to update attendance';
        showToast(msg, { kind: 'error' });
        return;
      }

      const isAttending = attendanceKey
        ? event.attendees?.includes(String(attendanceKey))
        : false;
      const result = await toggleAttendance(eventId, {
        isAttending,
        memberId: currentUserId,
      });

      if (result?.success === false) {
        const msg = result?.message || 'Failed to update attendance';
        showToast(msg, { kind: 'error' });
        return;
      }

      if (result?.event) {
        setEvents(prev => prev.map(item =>
          item.id === eventId ? result.event : item
        ));
        return;
      }

      // Fallback for mock path
      setEvents(prev => prev.map(item =>
        item.id === eventId
          ? {
              ...item,
              attendees: item.attendees?.includes(attendanceKey)
                ? item.attendees.filter(id => id !== attendanceKey)
                : [...(item.attendees ?? []), attendanceKey]
            }
          : item
      ));
    } catch (err) {
      const msg = err?.message || 'Failed to update attendance';
      showToast(msg, { kind: 'error' });
    }
  };

  const handleEventHover = (event) => {
    setHoverEvent(event);
  };

  const resolveAttendeeMembers = (event) => {
    const attendeeIds = (event?.attendees ?? []).map((id) => String(id));
    return attendeeIds
      .map((id) => membersById[id])
      .filter(Boolean);
  };

  const renderTimeline = (items, onCircleClick, isSearchResults = false) => (
    <div className="relative">
      <div className="absolute left-[12px] top-0 bottom-0 w-[1px] bg-gray-200" />
      <ul className="space-y-6">
        {items.map((event) => (
          <li
            key={event.id}
            className="relative flex items-center gap-3 py-1"
            onMouseEnter={() => {
              // On mobile, we use click-to-modal instead of hover-to-expand.
              if (windowWidth < 650 && !isSearchResults) return;

              const isMediumScreen = windowWidth >= 650 && windowWidth < 1025;
              if (isMediumScreen) {
                // In medium screen: only allow hover for search results when search is active
                if (isSearchActive) {
                  if (isSearchResults) handleEventHover(event);
                } else {
                  handleEventHover(event);
                }
              } else {
                // On other screens: always allow hover
                handleEventHover(event);
              }
            }}
            onMouseLeave={() => {
              if (windowWidth < 650 && !isSearchResults) return;

              const isMediumScreen = windowWidth >= 650 && windowWidth < 1025;
              if (isMediumScreen) {
                // In medium screen: only allow hover for search results when search is active
                if (isSearchActive) {
                  if (isSearchResults) setHoverEvent(null);
                } else {
                  setHoverEvent(null);
                }
              } else {
                // On other screens: always allow hover
                setHoverEvent(null);
              }
            }}
          >
            <button
              onClick={() => onCircleClick(event.id)}
              disabled={!(String(event.type ?? '').toLowerCase() === 'external' || String(event.type ?? '').toLowerCase() === 'public')}
              aria-label={`Toggle attendance for ${event.title}`}
              aria-pressed={Boolean(attendanceKey && event.attendees?.includes(String(attendanceKey)))}
              className={`relative z-10 flex items-center justify-center rounded-full shrink-0 top-[-1px] ${
                attendanceKey && event.attendees?.includes(String(attendanceKey))
                  ? 'h-6 w-6 bg-NBNA-primary text-white'
                  : (String(event.type ?? '').toLowerCase() === 'external' || String(event.type ?? '').toLowerCase() === 'public')
                    ? 'h-6 w-6 border border-gray-300 bg-white hover:border-NBNA-primary hover:text-NBNA-primary'
                    : 'h-6 w-6 border border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed'
              }`}
            >
              {attendanceKey && event.attendees?.includes(String(attendanceKey)) && (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>

            <div className="ml-4 flex-grow">
              {hoverEvent?.id === event.id ? (
                <>
                  {/* {console.log('Hover event debug:', {
                    eventId: event.id,
                    isSearchResults,
                    isSearchActive,
                    condition: ((isSearchResults && !isSearchActive) || (!isSearchResults && !isSearchActive))
                  })} */}
                  {((isSearchResults && isSearchActive) || (!isSearchResults && !isSearchActive)) && (
                    <div className="opacity-0 scale-95 animate-[fade-in_0.7s_ease-in-out_forwards]">
                      <EventCard event={event} attendeeMembers={resolveAttendeeMembers(event)} />
                    </div>
                  )}
                  {!((isSearchResults && isSearchActive) || (!isSearchResults && !isSearchActive)) && (
                    <a
                      href={event.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div className="font-playfair-sans text-NBNA-primary hover:text-NBNA-primary/70 transition-colors">
                        {event.title}
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(event.date).toLocaleDateString('en-CA', {
                          month: 'long',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                        })}
                      </div>
                      {event.type === 'External' && (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                          External
                        </span>
                      )}
                    </a>
                  )}
                </>
              ) : windowWidth < 650 && !isSearchResults ? (
                <button
                  type="button"
                  className="block w-full text-left"
                  onClick={() => setSelectedTimelineEvent(event)}
                >
                  <div
                    className="relative overflow-hidden rounded-lg border bg-white"
                    style={event.imageUrl ? { backgroundImage: `url(${event.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                  >
                    {event.imageUrl ? <div className="absolute inset-0 bg-black/40" /> : null}
                    <div className="relative p-3">
                      <div className={`font-playfair-sans text-xs ${event.imageUrl ? 'text-white' : 'text-NBNA-primary'} transition-colors`}>
                        {event.title}
                      </div>
                      <div className={`text-[10px] ${event.imageUrl ? 'text-white/90' : 'text-gray-500'}`}>
                        {new Date(event.date).toLocaleDateString('en-CA', {
                          month: 'long',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                        })}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {event.type === 'External' && (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                            External
                          </span>
                        )}
                        {String(event.type ?? '').toLowerCase().includes('exec') && (
                          <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                            Executive
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ) : (
                <a
                  href={event.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div
                    className="relative overflow-hidden rounded-lg border bg-white"
                    style={event.imageUrl ? { backgroundImage: `url(${event.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                  >
                    {event.imageUrl ? <div className="absolute inset-0 bg-black/40" /> : null}
                    <div className="relative p-3">
                      <div className={`font-playfair-sans ${event.imageUrl ? 'text-white' : 'text-NBNA-primary'} hover:text-NBNA-primary/70 transition-colors`}>
                        {event.title}
                      </div>
                      <div className={`text-sm ${event.imageUrl ? 'text-white/90' : 'text-gray-500'}`}>
                        {new Date(event.date).toLocaleDateString('en-CA', {
                          month: 'long',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                        })}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {event.type === 'External' && (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                            External
                          </span>
                        )}
                        {String(event.type ?? '').toLowerCase().includes('exec') && (
                          <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                            Executive
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  if (isLoading) {
    return (
      <div className="h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-NBNA-primary"></div>
      </div>
    );
  }

  const isMobile = windowWidth < 650;

  return (
    <div className="h-[calc(100vh-200px)] px-4 py-6">
      <div className="max-w-7xl mx-auto h-full">
        <h1 className="mb-4 text-2xl font-semibold text-NBNA-primary">Events</h1>
        {/* Executive chip removed */}
        {isMobile && userRole !== 'prospect' && (
          <div className="mb-4 flex gap-2">
            <button
              type="button"
              onClick={() => setMobilePanel('timeline')}
              className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${
                mobilePanel === 'timeline'
                  ? 'bg-NBNA-primary text-white border-NBNA-primary'
                  : 'bg-white text-NBNA-primary border-NBNA-primary/30'
              }`}
            >
              Upcoming
            </button>
            <button
              type="button"
              onClick={() => setMobilePanel('search')}
              className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${
                mobilePanel === 'search'
                  ? 'bg-NBNA-primary text-white border-NBNA-primary'
                  : 'bg-white text-NBNA-primary border-NBNA-primary/30'
              }`}
            >
              Search
            </button>
          </div>
        )}

        {/* Timeline modal (mobile) */}
        {selectedTimelineEvent && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedTimelineEvent(null);
            }}
          >
            <div className="w-full max-w-lg bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h4 className="font-playfair-sans text-base">Event Details</h4>
                <button
                  type="button"
                  onClick={() => setSelectedTimelineEvent(null)}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Close
                </button>
              </div>

              <div className="p-4">
                <EventCard event={selectedTimelineEvent} attendeeMembers={resolveAttendeeMembers(selectedTimelineEvent)} fullWidth />

                <div className="mt-4 flex items-center gap-3">
                  {(String(selectedTimelineEvent.type ?? '').toLowerCase() === 'external' || String(selectedTimelineEvent.type ?? '').toLowerCase() === 'public') ? (
                    <button
                      type="button"
                      onClick={async () => {
                        await handleAttendance(selectedTimelineEvent.id);
                        setSelectedTimelineEvent(null);
                      }}
                      className="btn-primary w-1/3"
                    >
                      {attendanceKey && selectedTimelineEvent.attendees?.includes(String(attendanceKey))
                        ? 'Not going'
                        : "I'm going"}
                    </button>
                  ) : (
                    <div className="text-sm text-gray-600">
                      Attendance for NBNA/internal events is via meeting QR sign-in.
                    </div>
                  )}

                  <div className="flex-1" />

                  {/* Attendee queue is shown in the card; keep modal controls minimal on mobile. */}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-8 h-full">
          {/* Event Timeline - Left Side */}
          {(!isMobile || mobilePanel === 'timeline') && (
            <div className={`w-full md:pr-8 h-full transition-all duration-300 ${
              isSearchActive || searchResults
                ? 'md:w-1/3 lg:w-1/2'
                : 'md:w-2/3 lg:w-1/2'
            }`}>
              <h2 className="mb-4 text-lg md:text-xl">Upcoming Events</h2>

              {/* Event Timeline */}
              <div className="h-[calc(100%-80px)] overflow-y-auto pr-4 pb-40">
                {renderTimeline(events, handleAttendance, false)}
              </div>
            </div>
          )}
          {/* Search Section - Right Side */}
          {userRole !== 'prospect' && (!isMobile || mobilePanel === 'search') && (
            <div className={`search-section w-full md:pl-8 md:border-l md:border-gray-200 h-full transition-all duration-300 ${
              isSearchActive || searchResults
                ? 'md:w-2/3 lg:w-1/2'
                : 'md:w-1/3 lg:w-1/2'
            }`}>
              {isSearching ? (
                <div className="flex flex-col items-center justify-center h-40">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-NBNA-primary mb-4"></div>
                  <p className="text-gray-600">Searching events with AI...</p>
                </div>
              ) : searchResults ? (
                <div className="h-full">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-playfair-sans">Search Results</h3>
                    <button
                      onClick={handleClearSearch}
                      className="text-sm text-NBNA-primary hover:text-NBNA-primary/70 transition-colors"
                    >
                      Clear Search
                    </button>
                  </div>

                  {Array.isArray(searchResults) && searchResults.length === 0 ? (
                    <p className="text-sm text-gray-500">No results.</p>
                  ) : (
                    <div className="w-full">
                      {/* Horizontal card scroll (mobile-first) */}
                      <div className="flex gap-4 overflow-x-auto pb-3 pr-1 snap-x snap-mandatory">
                        {(searchResults || []).map((event) => (
                          <div
                            key={event.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedSearchEvent(event)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedSearchEvent(event);
                              }
                            }}
                            className="snap-start cursor-pointer text-left min-w-[280px] max-w-[360px] w-[80%] sm:w-[320px] bg-white rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-NBNA-primary"
                          >
                            <EventCard event={event} attendeeMembers={resolveAttendeeMembers(event)} />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">Tap a card for details + Add.</p>
                    </div>
                  )}

                  {/* Modal */}
                  {selectedSearchEvent && (
                    <div
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                      role="dialog"
                      aria-modal="true"
                      onClick={(e) => {
                        if (e.target === e.currentTarget) setSelectedSearchEvent(null);
                      }}
                    >
                      <div className="w-full max-w-lg bg-white rounded-lg shadow-xl overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b">
                          <h4 className="font-playfair-sans text-base">Event Details</h4>
                          <button
                            type="button"
                            onClick={() => setSelectedSearchEvent(null)}
                            className="text-sm text-gray-600 hover:text-gray-900"
                          >
                            Close
                          </button>
                        </div>

                        <div className="p-4">
                          <EventCard event={selectedSearchEvent} attendeeMembers={resolveAttendeeMembers(selectedSearchEvent)} fullWidth />

                          <div className="mt-4 flex gap-2">
                            <button
                              type="button"
                              onClick={handleAddSelectedSearchEvent}
                              disabled={isAddingSearchEvent}
                              className="btn-primary flex-1 disabled:opacity-50"
                            >
                              {isAddingSearchEvent ? 'Adding…' : 'Add Event'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setSelectedSearchEvent(null)}
                              className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <h3 className="mb-4 font-playfair-sans">Search Events</h3>
                  <form onSubmit={handleSearch} className="space-y-3">
                    <div>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setIsSearchActive(true);
                        }}
                        onFocus={() => setIsSearchActive(true)}
                        placeholder="Search for events in Nanaimo..."
                        className="input-field w-full text-sm"
                      />
                    </div>
                    <div className="flex flex-col md:flex-col sm:flex-row lg:flex-row gap-2">
                      <select
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                        className="select-field w-full md:w-full sm:w-2/3 lg:w-2/3 text-sm"
                      >
                        <option value="6weeks">Next 6 weeks</option>
                        <option value="quarter">Next quarter</option>
                        <option value="6months">Next 6 months</option>
                      </select>
                      <button
                        type="submit"
                        className="btn-primary w-full md:w-full sm:w-1/3 lg:w-1/3 text-sm py-1.5"
                      >
                        Search
                      </button>
                    </div>
                  </form>
                  
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
